import {
  getMediaProxyBaseCandidates as buildMediaProxyBaseCandidates,
  DEFAULT_CLOUD_LIGHT_MEDIA_PROXY,
  getBillingMediaProxyCandidates,
  getDevServerMediaProxyBase,
  isDevServerMediaProxyBase,
} from './mediaProxyConfig';
import { AUTH_SESSION_HEADER, readStoredAuthSessionId } from './authResolverClient';
import { trackResolverRequest } from './analytics';
import { parseResolverFeaturesFromHealthBody } from './resolverFeatures';
import { pickAuthResolverBase, resolveStickyAuthBase } from './authResolverClient';
import { tryRefreshAccessToken } from './googleLoginRefreshRegistry';
import { getActiveProviderHeaders, loadProviderSettings } from './providerSettings';
import { getYoutubeEgressHeaders, getScrapeProxyHeaders } from './youtubeUnlock';
import {
  isMusicCollectionByEntryUri,
  isMusicCollectionLinkUri,
  musicCollectionPlaybackProxyPathFromLink,
  musicCollectionPlaybackProxyPathFromUri,
} from './musicCollectionLinkUtils';
import { isBandcampLinkUri, repairBandcampLinkUri } from './bandcampLinkUtils';
import { isArchiveOrgLinkUri, isArchiveOrgDirectDownloadUri } from './archiveOrgLinkUtils';
import { isLocGovLinkUri } from './locGovLinkUtils';
import { isOwnedMediaLinkUri } from './linkRecording';
import { isLocalhostCastBase, setCastPublicBaseFromHealth } from './castSupport';
import { isNavigatorOffline } from './offlineNetwork';

let activeProxyBase = null;
let heavyMlProxyBase = null;
let snapcastPlaybackProxyBase = null;
let castPlaybackProxyBase = null;
let midiImportProxyBase = null;
let musicCollectionProxyBase = null;
let billingProxyBase = null;
let billingAdminProxyBase = null;
let lastProbeCandidates = [];

// Health checks must fail fast. A configured-but-unreachable candidate (e.g. the
// public resolver when the browser can't reach it via NAT loopback) would
// otherwise hang for ~70s on the browser's default connect timeout, blocking the
// whole probe and hiding resolver-backed UI until it eventually fails.
const HEALTH_TIMEOUT_MS = 6000;

export function getMediaProxyBaseCandidates() {
  return buildMediaProxyBaseCandidates();
}

function fetchWithTimeout(url, options, timeoutMs) {
  if (typeof AbortController === 'undefined') {
    return fetch(url, options);
  }
  const controller = new AbortController();
  const timer = setTimeout(function() { controller.abort(); }, timeoutMs);
  const merged = Object.assign({}, options, { signal: controller.signal });
  return fetch(url, merged).finally(function() {
    clearTimeout(timer);
  });
}

export function getMediaProxyBase() {
  if (activeProxyBase) return activeProxyBase;
  const candidates = getMediaProxyBaseCandidates();
  return candidates.length > 0 ? candidates[0] : '';
}

export function getActiveMediaProxyBase() {
  return activeProxyBase || '';
}

export function getHeavyMlMediaProxyBase() {
  return heavyMlProxyBase || activeProxyBase || '';
}

export function getMusicCollectionMediaProxyBase() {
  return musicCollectionProxyBase || '';
}

function isLikelyLocalResolverBase(base) {
  try {
    const host = new URL(base).hostname;
    return host === 'localhost'
      || host === '127.0.0.1'
      || host.startsWith('192.168.')
      || host.startsWith('10.')
      || host.startsWith('172.');
  } catch (e) {
    return false;
  }
}

function candidateAiEnabled(base) {
  const list = lastProbeCandidates || [];
  for (let i = 0; i < list.length; i++) {
    if (list[i] && list[i].base === base) {
      return !!(list[i].features && list[i].features.ai === true);
    }
  }
  return false;
}

function candidateHasHeavyMl(candidate) {
  const f = candidate && candidate.features;
  if (!f) return false;
  return !!(f.stems || f.sheetImage || f.sheetImageOmr || f.practiceAnalysis || f.practiceTrack);
}

function pickHeavyMlBase(candidates) {
  let fallback = null;
  for (let i = 0; i < candidates.length; i++) {
    const c = candidates[i];
    if (!c.reachable || !c.available) continue;
    if (c.resolverAccess === false) continue;
    if (c.resolverAccess === false) continue;
    if (!candidateHasHeavyMl(c)) continue;
    if (isLikelyLocalResolverBase(c.base)) return c.base;
    if (!fallback) fallback = c.base;
  }
  return fallback;
}

function candidateHasMusicCollectionHost(candidate) {
  const f = candidate && candidate.features;
  return !!(f && f.musicCollection);
}

function pickMusicCollectionBase(candidates) {
  let remoteFallback = null;
  for (let i = 0; i < candidates.length; i++) {
    const c = candidates[i];
    if (!c.reachable || !c.musicCollectionAccess) continue;
    if (!candidateHasMusicCollectionHost(c)) continue;
    if (isCloudLightResolverBase(c.base)) continue;
    if (isLikelyLocalResolverBase(c.base)) return c.base;
    if (!remoteFallback) remoteFallback = c.base;
  }
  return remoteFallback;
}

/** Hosted resolver ledger (Cloud Run) — not peppertrees or local dev. */
function findCloudBillingCandidate(candidates) {
  const preferred = getBillingMediaProxyCandidates() || [];
  for (let i = 0; i < preferred.length; i++) {
    const base = preferred[i];
    for (let j = 0; j < candidates.length; j++) {
      const c = candidates[j];
      if (c.base === base && c.reachable && c.billingEnabled) {
        return c;
      }
    }
  }
  return null;
}

function findBillingHealthCandidate(candidates, proxyBase) {
  if (proxyBase && isDevServerMediaProxyBase(proxyBase)) {
    const cloud = findCloudBillingCandidate(candidates);
    if (cloud) return cloud;
  }
  if (proxyBase) {
    return candidates.find(function(c) { return c.base === proxyBase; }) || null;
  }
  return findCloudBillingCandidate(candidates);
}

export function pickBillingProxyBase(candidates) {
  const cloudBilling = findCloudBillingCandidate(candidates);

  if (process.env.NODE_ENV === 'development') {
    const devBase = getDevServerMediaProxyBase();
    if (devBase && cloudBilling) {
      for (let j = 0; j < candidates.length; j++) {
        const c = candidates[j];
        if (c.base === devBase && c.reachable) {
          return devBase;
        }
      }
    }
  }

  if (cloudBilling) {
    return cloudBilling.base;
  }

  const preferred = getBillingMediaProxyCandidates() || [];
  for (let i = 0; i < preferred.length; i++) {
    const base = preferred[i];
    for (let j = 0; j < candidates.length; j++) {
      const c = candidates[j];
      if (c.base === base && c.reachable && c.billingEnabled) {
        return c.base;
      }
    }
  }
  let localFallback = null;
  for (let i = 0; i < candidates.length; i++) {
    const c = candidates[i];
    if (!c.reachable || !c.billingEnabled) continue;
    if (!isLikelyLocalResolverBase(c.base)) return c.base;
    if (!localFallback) localFallback = c.base;
  }
  return localFallback;
}

function findCloudBillingAdminCandidate(candidates) {
  const preferred = getBillingMediaProxyCandidates() || [];
  for (let i = 0; i < preferred.length; i++) {
    const base = preferred[i];
    for (let j = 0; j < candidates.length; j++) {
      const c = candidates[j];
      if (c.base === base && c.reachable && c.billingEnabled && c.adminAccess) {
        return c;
      }
    }
  }
  return null;
}

function pickBillingAdminBase(candidates) {
  const cloudAdmin = findCloudBillingAdminCandidate(candidates);

  if (process.env.NODE_ENV === 'development') {
    const devBase = getDevServerMediaProxyBase();
    if (devBase && cloudAdmin) {
      for (let j = 0; j < candidates.length; j++) {
        const c = candidates[j];
        if (c.base === devBase && c.reachable) {
          return devBase;
        }
      }
    }
  }

  if (cloudAdmin) {
    return cloudAdmin.base;
  }

  const preferred = getBillingMediaProxyCandidates() || [];
  for (let i = 0; i < preferred.length; i++) {
    const base = preferred[i];
    for (let j = 0; j < candidates.length; j++) {
      const c = candidates[j];
      if (c.base === base && c.reachable && c.adminAccess && c.billingEnabled) {
        return c.base;
      }
    }
  }
  let localFallback = null;
  for (let i = 0; i < candidates.length; i++) {
    const c = candidates[i];
    if (!c.reachable || !c.adminAccess || !c.billingEnabled) continue;
    if (!isLikelyLocalResolverBase(c.base)) return c.base;
    if (!localFallback) localFallback = c.base;
  }
  return localFallback;
}

export function getBillingProxyBase() {
  if (billingProxyBase) return billingProxyBase;
  if (lastProbeCandidates.length > 0) {
    const resolved = pickBillingProxyBase(lastProbeCandidates);
    if (resolved) {
      billingProxyBase = resolved;
      return resolved;
    }
  }
  return '';
}

function billingFieldsFromCandidate(candidate) {
  if (!candidate) {
    return {
      billingEnabled: false,
      purchasesEnabled: false,
      creditRequired: false,
      creditBalanceCents: null,
      creditUnlimited: false,
    };
  }
  return {
    billingEnabled: !!candidate.billingEnabled,
    purchasesEnabled: candidate.purchasesEnabled === true,
    creditRequired: !!candidate.creditRequired,
    creditBalanceCents: typeof candidate.creditBalanceCents === 'number'
      ? candidate.creditBalanceCents
      : null,
    creditUnlimited: !!candidate.creditUnlimited,
  };
}

export function hasMusicCollectionAccess(candidates) {
  if (!Array.isArray(candidates)) return false;
  for (let i = 0; i < candidates.length; i++) {
    if (candidates[i].musicCollectionAccess) return true;
  }
  return false;
}

export function hasAdminAccess(candidates) {
  if (!Array.isArray(candidates)) return false;
  for (let i = 0; i < candidates.length; i++) {
    if (candidates[i].adminAccess) return true;
  }
  return false;
}

export function hasBillingAdminAccess(candidates) {
  return !!pickBillingAdminBase(candidates);
}

export function getBillingAdminProxyBase() {
  if (billingAdminProxyBase) return billingAdminProxyBase;
  if (lastProbeCandidates.length > 0) {
    const resolved = pickBillingAdminBase(lastProbeCandidates);
    if (resolved) {
      billingAdminProxyBase = resolved;
      return resolved;
    }
  }
  return '';
}

export function isMediaProxyConfigured() {
  return getMediaProxyBaseCandidates().length > 0;
}

export function clearActiveMediaProxyBase() {
  // Only clear the last successful fetch target. Playback routing bases come from
  // the last /health probe and are refreshed in probeMediaResolverCandidates;
  // wiping them here caused cast/snapcast requests to fail mid-session while the
  // Settings UI still showed a resolver as "in use".
  activeProxyBase = null;
}

export function isCloudLightResolverBase(base) {
  if (!base) return false;
  if (base === DEFAULT_CLOUD_LIGHT_MEDIA_PROXY) return true;
  return /resolver-light/i.test(String(base));
}

function candidateHasSnapcastPlayback(candidate) {
  const features = candidate && candidate.features;
  return !!(features && features.snapcastPlayback);
}

export function resolveSnapcastPlaybackBase(candidates) {
  const preferredAuth = pickAuthResolverBase(candidates);
  if (preferredAuth) {
    for (let i = 0; i < candidates.length; i++) {
      const candidate = candidates[i];
      if (candidate.base !== preferredAuth) continue;
      if (!candidate.reachable || !candidate.available) continue;
      if (candidate.resolverAccess === false) continue;
      if (!candidateHasSnapcastPlayback(candidate)) continue;
      return candidate.base;
    }
  }
  let remoteFallback = null;
  for (let i = 0; i < candidates.length; i++) {
    const candidate = candidates[i];
    if (!candidate.reachable || !candidate.available) continue;
    if (candidate.resolverAccess === false) continue;
    if (!candidateHasSnapcastPlayback(candidate)) continue;
    if (isCloudLightResolverBase(candidate.base)) continue;
    if (isLikelyLocalResolverBase(candidate.base)) return candidate.base;
    if (!remoteFallback) remoteFallback = candidate.base;
  }
  return remoteFallback;
}

export function getSnapcastPlaybackProxyBase() {
  if (snapcastPlaybackProxyBase) return snapcastPlaybackProxyBase;
  if (lastProbeCandidates.length > 0) {
    const resolved = resolveSnapcastPlaybackBase(lastProbeCandidates);
    if (resolved) {
      snapcastPlaybackProxyBase = resolved;
      return resolved;
    }
  }
  return '';
}

function candidateHasCastPlayback(candidate) {
  const features = candidate && candidate.features;
  if (features && features.castPlayback) return true;
  const cast = candidate && candidate.cast;
  return !!(cast && cast.enabled !== false && cast.publicBase);
}

function candidateSupportsCastPlayback(candidate, candidates) {
  if (candidateHasCastPlayback(candidate)) return true;
  if (!isDevServerMediaProxyBase(candidate.base)) return false;
  for (let i = 0; i < candidates.length; i++) {
    const loopback = candidates[i];
    if (loopback.base !== 'http://localhost:8787' && loopback.base !== 'http://127.0.0.1:8787') {
      continue;
    }
    if (candidateHasCastPlayback(loopback)) return true;
  }
  return false;
}

function preferDirectLocalResolverBase(base, candidates) {
  if (!isDevServerMediaProxyBase(base)) return base;
  for (let i = 0; i < candidates.length; i++) {
    const direct = candidates[i];
    if (direct.base !== 'http://localhost:8787' && direct.base !== 'http://127.0.0.1:8787') {
      continue;
    }
    if (!direct.reachable || !direct.available || direct.resolverAccess === false) continue;
    if (!candidateSupportsCastPlayback(direct, candidates)) continue;
    return direct.base;
  }
  return base;
}

export function resolveCastPlaybackBase(candidates) {
  const preferredAuth = pickAuthResolverBase(candidates);
  if (preferredAuth) {
    for (let i = 0; i < candidates.length; i++) {
      const candidate = candidates[i];
      if (candidate.base !== preferredAuth) continue;
      if (!candidate.reachable || !candidate.available) continue;
      if (candidate.resolverAccess === false) continue;
      if (!candidateSupportsCastPlayback(candidate, candidates)) continue;
      return preferDirectLocalResolverBase(candidate.base, candidates);
    }
  }
  let remoteFallback = null;
  for (let i = 0; i < candidates.length; i++) {
    const candidate = candidates[i];
    if (!candidate.reachable || !candidate.available) continue;
    if (candidate.resolverAccess === false) continue;
    if (!candidateSupportsCastPlayback(candidate, candidates)) continue;
    if (isCloudLightResolverBase(candidate.base)) continue;
    if (isLikelyLocalResolverBase(candidate.base)) {
      return preferDirectLocalResolverBase(candidate.base, candidates);
    }
    if (!remoteFallback) remoteFallback = candidate.base;
  }
  return remoteFallback;
}

export function getCastPlaybackProxyBase() {
  if (castPlaybackProxyBase) return castPlaybackProxyBase;
  if (lastProbeCandidates.length > 0) {
    const resolved = resolveCastPlaybackBase(lastProbeCandidates);
    if (resolved) {
      castPlaybackProxyBase = resolved;
      return resolved;
    }
  }
  return '';
}

function pickMidiImportBase(candidates) {
  let remoteFallback = null;
  let anyFallback = null;
  for (let i = 0; i < candidates.length; i++) {
    const c = candidates[i];
    if (!c.reachable || !c.available) continue;
    if (c.resolverAccess === false) continue;
    if (!anyFallback) anyFallback = c.base;
    if (isCloudLightResolverBase(c.base)) continue;
    if (isLikelyLocalResolverBase(c.base)) return c.base;
    if (!remoteFallback) remoteFallback = c.base;
  }
  return remoteFallback || anyFallback;
}

export function normalizeAccessToken(accessToken) {
  if (!accessToken) return '';
  if (typeof accessToken === 'string') return accessToken;
  if (typeof accessToken === 'object' && typeof accessToken.access_token === 'string') {
    return accessToken.access_token;
  }
  return '';
}

function buildAuthHeaders(accessToken) {
  const headers = {};
  const token = normalizeAccessToken(accessToken);
  if (token) {
    headers.Authorization = 'Bearer ' + token;
  }
  const sessionId = readStoredAuthSessionId();
  if (sessionId) {
    headers[AUTH_SESSION_HEADER] = sessionId;
  }
  return headers;
}

function isMixedContentBlocked(base) {
  if (typeof window === 'undefined' || !window.location) return false;
  if (window.location.protocol !== 'https:') return false;
  return /^http:\/\//i.test(base);
}

function detectMixedContent(bases) {
  const baseList = Array.isArray(bases) ? bases : [bases];
  return baseList.some(isMixedContentBlocked);
}

function notifyResolverUnreachable() {
  // Resolver health is cached and not re-checked on every request, so it can
  // report "available" while the resolver is actually down. When a proxied
  // request fails to reach any base, ask listeners (the health store) to
  // re-probe so the UI stops claiming the resolver is available.
  if (typeof window !== 'undefined' && typeof window.dispatchEvent === 'function') {
    try {
      window.dispatchEvent(new Event('mediaProxyUnreachable'));
    } catch (e) {
      // Older environments without the Event constructor: ignore.
    }
  }
}

function wrapFetchError(error, bases) {
  const baseList = Array.isArray(bases) ? bases : [bases];
  if (error && error.name === 'TypeError' && String(error.message).indexOf('fetch') >= 0) {
    activeProxyBase = null;
    notifyResolverUnreachable();
    if (detectMixedContent(baseList)) {
      throw new Error(
        'Could not reach the media resolver because this page is served over HTTPS '
        + 'but the resolver uses HTTP (' + baseList.join(', ') + '). '
        + 'Browsers block these "mixed content" requests. Serve the resolver over HTTPS '
        + 'or open the app over HTTP to use it.'
      );
    }
    throw new Error(
      'Could not reach any media resolver (tried: ' + baseList.join(', ') + '). '
      + (baseList.length === 1 && String(baseList[0]).indexOf('run.app') >= 0
        ? 'On localhost, restart the dev server (npm start) so /billing proxies to Cloud Run, and ensure the local resolver is running on :8787 for other features.'
        : 'Start the local resolver with: cd local-resolver && docker compose up --build')
    );
  }
  throw error;
}

function resolverEndpointForPath(pathAndQuery) {
  if (!pathAndQuery) return '';
  if (pathAndQuery.indexOf('/proxy-audio') === 0) return 'proxy-audio';
  if (pathAndQuery.indexOf('/youtube/') === 0) return 'youtube-audio';
  if (pathAndQuery.indexOf('/bandcamp/audio') === 0) return 'bandcamp-audio';
  if (pathAndQuery.indexOf('/internet-archive/audio') === 0) return 'internet-archive-audio';
  if (pathAndQuery.indexOf('/loc/audio') === 0) return 'loc-audio';
  if (pathAndQuery.indexOf('/detect-chords') === 0) return 'detect-chords';
  if (pathAndQuery.indexOf('/detect-playback-region') === 0) return 'detect-playback-region';
  if (pathAndQuery.indexOf('/analyze-media') === 0) return 'analyze-media';
  if (pathAndQuery.indexOf('/search-lyrics') === 0) return 'search-lyrics';
  if (pathAndQuery.indexOf('/lyrics-dictionary') === 0) return 'lyrics-dictionary';
  if (pathAndQuery.indexOf('/lyrics-thesaurus') === 0) return 'lyrics-thesaurus';
  if (pathAndQuery.indexOf('/lyrics-rhyme') === 0) return 'lyrics-rhyme';
  if (pathAndQuery.indexOf('/lyrics-reverse-dictionary') === 0) return 'lyrics-reverse-dictionary';
  if (pathAndQuery.indexOf('/lyrics-phrases') === 0) return 'lyrics-phrases';
  if (pathAndQuery.indexOf('/lyrics-alliteration') === 0) return 'lyrics-alliteration';
  if (pathAndQuery.indexOf('/search-chords') === 0) return 'search-chords';
  if (pathAndQuery.indexOf('/search-notation') === 0) return 'search-notation';
  if (pathAndQuery.indexOf('/search-similar-melodies') === 0) return 'search-similar-melodies';
  if (pathAndQuery.indexOf('/search-music-collection') === 0) return 'search-music-collection';
  if (pathAndQuery.indexOf('/browse-music-collection') === 0) return 'browse-music-collection';
  if (pathAndQuery.indexOf('/music-collection-duplicates') === 0) return 'music-collection-duplicates';
  if (pathAndQuery.indexOf('/music-collection-triage') === 0) return 'music-collection-triage';
  if (pathAndQuery.indexOf('/music-collection-move-plan') === 0) return 'music-collection-move-plan';
  if (pathAndQuery.indexOf('/music-collection-registry') === 0) return 'music-collection-registry';
  if (pathAndQuery.indexOf('/music-collection-artists') === 0) return 'music-collection-artists';
  if (pathAndQuery.indexOf('/music-collection-albums') === 0) return 'music-collection-albums';
  if (pathAndQuery.indexOf('/music-collection-genres') === 0) return 'music-collection-genres';
  if (pathAndQuery.indexOf('/music-collection-tree') === 0) return 'music-collection-tree';
  if (pathAndQuery.indexOf('/music-collection-chunks') === 0) return 'music-collection-chunks';
  if (pathAndQuery.indexOf('/music-collection-triage/bulk') === 0) return 'music-collection-triage-bulk';
  if (pathAndQuery.indexOf('/search-bandcamp') === 0) return 'search-bandcamp';
  if (pathAndQuery.indexOf('/search-internet-archive') === 0) return 'search-internet-archive';
  if (pathAndQuery.indexOf('/search-europeana') === 0) return 'search-europeana';
  if (pathAndQuery.indexOf('/search-loc-audio') === 0) return 'search-loc-audio';
  if (pathAndQuery.indexOf('/rebuild-music-collection-index') === 0) return 'rebuild-music-collection-index';
  if (pathAndQuery.indexOf('/music-collection-by-entry/') === 0) return 'music-collection-by-entry';
  if (pathAndQuery.indexOf('/music-collection/') === 0) return 'music-collection';
  if (pathAndQuery.indexOf('/music-collection-art/') === 0) return 'music-collection-art';
  if (pathAndQuery.indexOf('/research-tune-background') === 0) return 'research-tune-background';
  if (pathAndQuery.indexOf('/generate-feed-articles') === 0) return 'generate-feed-articles';
  if (pathAndQuery.indexOf('/generate-feed-quizzes') === 0) return 'generate-feed-quizzes';
  if (pathAndQuery.indexOf('/enrich-feed-sources') === 0) return 'enrich-feed-sources';
  if (pathAndQuery.indexOf('/help-query') === 0) return 'help-query';
  if (pathAndQuery.indexOf('/discover-composer') === 0) return 'discover-composer';
  if (pathAndQuery.indexOf('/discover-genre') === 0) return 'discover-genre';
  if (pathAndQuery.indexOf('/separate-stems') === 0) return 'separate-stems';
  if (pathAndQuery.indexOf('/generate-practice-track') === 0) return 'generate-practice-track';
  if (pathAndQuery.indexOf('/generate-audio') === 0) return 'generate-audio';
  if (pathAndQuery.indexOf('/render-midi') === 0) return 'render-midi';
  if (pathAndQuery.indexOf('/transcribe-sheet-image') === 0) return 'transcribe-sheet-image';
  if (pathAndQuery.indexOf('/extract-sheet-metadata') === 0) return 'extract-sheet-metadata';
  if (pathAndQuery.indexOf('/split-sheet-page') === 0) return 'split-sheet-page';
  if (pathAndQuery.indexOf('/search-images') === 0) return 'search-images';
  if (pathAndQuery.indexOf('/midi2abc') === 0) return 'midi2abc';
  if (pathAndQuery.indexOf('/midi2xml') === 0) return 'midi2xml';
  if (pathAndQuery.indexOf('/midi-resources/') === 0) return 'midi-resources';
  if (pathAndQuery.indexOf('/score2xml') === 0) return 'score2xml';
  if (pathAndQuery.indexOf('/abc2xml') === 0) return 'abc2xml';
  if (/^\/stems\/[^/]+\/status/.test(pathAndQuery)) return 'stem-status';
  if (pathAndQuery.indexOf('/stems/') === 0) return 'stem-audio';
  return '';
}

function unreachableHealthResult(base) {
  return {
    base: base,
    reachable: false,
    available: false,
    requireAuth: false,
    authReason: '',
    mixedContent: isMixedContentBlocked(base),
    oauthBff: false,
    embeddedCreds: false,
    billingEnabled: false,
    creditRequired: false,
    creditBalanceCents: null,
    creditUnlimited: false,
    resolverAccess: false,
    adminAccess: false,
    musicCollectionAccess: false,
    reviewProjects: false,
    providers: null,
    lightMode: false,
  };
}

async function tryHealthAtBase(base, accessToken) {
  // An HTTPS page can never reach an http:// resolver. Skip the fetch entirely:
  // besides being pointless, requests to http://localhost from a public HTTPS
  // origin can get stuck pending in some browsers (mixed-content / private
  // network access gating) without ever rejecting, which would hang the probe.
  if (isMixedContentBlocked(base)) {
    return unreachableHealthResult(base);
  }

  const headers = { Accept: 'application/json' };
  if (accessToken) {
    headers.Authorization = 'Bearer ' + accessToken;
  }
  const sessionId = readStoredAuthSessionId();
  if (sessionId) {
    headers[AUTH_SESSION_HEADER] = sessionId;
  }

  try {
    const response = await fetchWithTimeout(base + '/health', {
      cache: 'no-store',
      headers: headers,
    }, HEALTH_TIMEOUT_MS);
    if (!response.ok) {
      return {
        base: base,
        reachable: false,
        available: false,
        requireAuth: false,
        authReason: '',
        mixedContent: false,
        oauthBff: false,
      };
    }

    const contentType = response.headers.get('content-type') || '';
    if (contentType.indexOf('application/json') === -1) {
      return {
        base: base,
        reachable: false,
        available: false,
        requireAuth: false,
        authReason: '',
        mixedContent: false,
        oauthBff: false,
      };
    }

    const body = await response.json();
    if (!body || !body.ok) {
      return {
        base: base,
        reachable: false,
        available: false,
        requireAuth: false,
        authReason: '',
        mixedContent: false,
        oauthBff: false,
      };
    }

    const requireAuth = !!body.requireAuth;
    let available = true;
    let authReason = body.authReason || '';

    if (requireAuth) {
      if (body.authorized === true) {
        available = true;
      } else {
        available = false;
        authReason = authReason || 'login_required';
      }
    }

    const features = parseResolverFeaturesFromHealthBody(body);
    const oauthBff = body.oauthBff === true || features.oauthBff === true;
    if (oauthBff) features.oauthBff = true;
    if (body.soundfontsReady === true || body.soundfontsReady === false) {
      features.soundfonts = true;
    }

    return {
      base: base,
      reachable: true,
      available: available,
      requireAuth: requireAuth,
      authReason: authReason,
      mixedContent: false,
      demucsModel: typeof body.demucsModel === 'string' ? body.demucsModel : 'htdemucs',
      demucsStems: Array.isArray(body.demucsStems) ? body.demucsStems : null,
      features: features,
      oauthBff: oauthBff,
      embeddedCreds: body.embeddedCreds === true,
      billingEnabled: body.billingEnabled === true,
      purchasesEnabled: body.purchasesEnabled === true,
      creditRequired: body.creditRequired === true,
      creditBalanceCents: typeof body.creditBalanceCents === 'number' ? body.creditBalanceCents : null,
      creditUnlimited: body.creditUnlimited === true,
      resolverAccess: body.resolverAccess !== false,
      adminAccess: body.adminAccess === true,
      musicCollectionAccess: body.musicCollectionAccess === true,
      providers: body.providers && typeof body.providers === 'object' ? body.providers : null,
      lightMode: body.lightMode === true || features.lightMode === true,
      soundfontsReady: body.soundfontsReady === true,
      soundfontsProgress: body.soundfontsProgress && typeof body.soundfontsProgress === 'object'
        ? body.soundfontsProgress
        : null,
      soundfontsRunning: body.soundfontsRunning === true,
      musicCollectionCount: typeof body.musicCollectionCount === 'number'
        ? body.musicCollectionCount
        : 0,
      musicCollectionDir: typeof body.musicCollectionDir === 'string' ? body.musicCollectionDir : null,
      musicCollectionIndex: typeof body.musicCollectionIndex === 'string' ? body.musicCollectionIndex : null,
      musicCollectionStats: typeof body.musicCollectionStats === 'string' ? body.musicCollectionStats : null,
      musicCollectionBuiltAt: typeof body.musicCollectionBuiltAt === 'string' ? body.musicCollectionBuiltAt : null,
      musicCollectionSummary: body.musicCollectionSummary && typeof body.musicCollectionSummary === 'object'
        ? body.musicCollectionSummary
        : null,
      reviewProjects: body.reviewProjects === true,
      reviewProjectsDir: typeof body.reviewProjectsDir === 'string' ? body.reviewProjectsDir : null,
      reviewProjectsCount: typeof body.reviewProjectsCount === 'number' ? body.reviewProjectsCount : 0,
      practiceTrackBackend: body.practiceTrackBackend && typeof body.practiceTrackBackend === 'object'
        ? body.practiceTrackBackend
        : null,
      snapcast: body.snapcast && typeof body.snapcast === 'object' ? body.snapcast : null,
      cast: body.cast && typeof body.cast === 'object' ? body.cast : null,
    };
  } catch (e) {
    return {
      base: base,
      reachable: false,
      available: false,
      requireAuth: false,
      authReason: '',
      mixedContent: isMixedContentBlocked(base),
      oauthBff: false,
    };
  }
}

// Guarantees the probe for a single base always settles, even if the underlying
// fetch never resolves or its AbortController fails to reject (observed for
// blocked cross-origin/local requests on some browsers). Without this a single
// stuck candidate would hang the whole Promise.all and the Settings page would
// show "Checking resolvers..." forever.
function probeBaseWithHardTimeout(base, accessToken) {
  return new Promise(function(resolve) {
    let settled = false;
    const timer = setTimeout(function() {
      if (settled) return;
      settled = true;
      resolve(unreachableHealthResult(base));
    }, HEALTH_TIMEOUT_MS + 1000);

    function finish(result) {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      resolve(result);
    }

    tryHealthAtBase(base, accessToken).then(finish, function() {
      finish(unreachableHealthResult(base));
    });
  });
}

export async function probeMediaResolverCandidates(accessToken) {
  const bases = getMediaProxyBaseCandidates();
  lastProbeCandidates = [];

  // Probe all candidates concurrently so one slow/unreachable base (typically
  // the public resolver) can't block the others. Promise.all preserves order,
  // so the active base is still chosen by candidate priority.
  const candidates = await Promise.all(bases.map(function(base) {
    return probeBaseWithHardTimeout(base, accessToken);
  }));
  lastProbeCandidates = candidates;

  let activeBase = null;
  let activeCandidate = null;
  for (let i = 0; i < candidates.length; i++) {
    if (candidates[i].reachable && candidates[i].available) {
      activeBase = candidates[i].base;
      activeCandidate = candidates[i];
      break;
    }
  }

  if (activeCandidate && activeCandidate.cast && activeCandidate.cast.publicBase) {
    setCastPublicBaseFromHealth(activeCandidate.cast.publicBase);
  } else if (activeBase && !isLocalhostCastBase(activeBase)) {
    setCastPublicBaseFromHealth(activeBase);
  } else {
    setCastPublicBaseFromHealth(null);
  }

  heavyMlProxyBase = pickHeavyMlBase(candidates);
  snapcastPlaybackProxyBase = resolveSnapcastPlaybackBase(candidates);
  castPlaybackProxyBase = resolveCastPlaybackBase(candidates);
  midiImportProxyBase = pickMidiImportBase(candidates);
  musicCollectionProxyBase = pickMusicCollectionBase(candidates);
  billingProxyBase = pickBillingProxyBase(candidates);
  billingAdminProxyBase = pickBillingAdminBase(candidates);
  const billingCandidate = findBillingHealthCandidate(candidates, billingProxyBase);
  const billingFields = billingFieldsFromCandidate(billingCandidate);
  const preferredAuthBase = pickAuthResolverBase(candidates);
  const authBase = resolveStickyAuthBase(candidates, null);
  const collectionCandidate = musicCollectionProxyBase
    ? candidates.find(function(c) { return c.base === musicCollectionProxyBase; })
    : null;
  const collectionFieldsSource = collectionCandidate || activeCandidate;
  return {
    available: !!activeBase,
    activeBase: activeBase,
    heavyMlBase: heavyMlProxyBase,
    snapcastPlaybackBase: snapcastPlaybackProxyBase,
    castPlaybackBase: castPlaybackProxyBase,
    musicCollectionBase: musicCollectionProxyBase,
    authBase: authBase,
    preferredAuthBase: preferredAuthBase,
    candidates: candidates,
    demucsModel: activeCandidate && activeCandidate.demucsModel
      ? activeCandidate.demucsModel
      : 'htdemucs',
    demucsStems: activeCandidate && activeCandidate.demucsStems
      ? activeCandidate.demucsStems
      : null,
    features: activeCandidate && activeCandidate.features
      ? activeCandidate.features
      : null,
    embeddedCreds: activeCandidate ? !!activeCandidate.embeddedCreds : false,
    billingEnabled: billingFields.billingEnabled,
    purchasesEnabled: billingFields.purchasesEnabled,
    creditRequired: billingFields.creditRequired,
    creditBalanceCents: billingFields.creditBalanceCents,
    creditUnlimited: billingFields.creditUnlimited,
    billingProxyBase: billingProxyBase || '',
    resolverAccess: activeCandidate ? activeCandidate.resolverAccess !== false : false,
    adminAccess: hasAdminAccess(candidates),
    billingAdminAccess: hasBillingAdminAccess(candidates),
    musicCollectionAccess: hasMusicCollectionAccess(candidates),
    reviewProjects: !!(activeCandidate && activeCandidate.reviewProjects),
    reviewProjectsDir: activeCandidate && activeCandidate.reviewProjectsDir
      ? activeCandidate.reviewProjectsDir
      : null,
    reviewProjectsCount: activeCandidate && typeof activeCandidate.reviewProjectsCount === 'number'
      ? activeCandidate.reviewProjectsCount
      : 0,
    requireAuth: activeCandidate ? !!activeCandidate.requireAuth : false,
    authReason: activeCandidate ? (activeCandidate.authReason || '') : '',
    providers: activeCandidate && activeCandidate.providers
      ? activeCandidate.providers
      : null,
    musicCollectionCount: collectionFieldsSource && typeof collectionFieldsSource.musicCollectionCount === 'number'
      ? collectionFieldsSource.musicCollectionCount
      : 0,
    musicCollectionDir: collectionFieldsSource && collectionFieldsSource.musicCollectionDir
      ? collectionFieldsSource.musicCollectionDir
      : null,
    musicCollectionIndex: collectionFieldsSource && collectionFieldsSource.musicCollectionIndex
      ? collectionFieldsSource.musicCollectionIndex
      : null,
    musicCollectionStats: collectionFieldsSource && collectionFieldsSource.musicCollectionStats
      ? collectionFieldsSource.musicCollectionStats
      : null,
    musicCollectionBuiltAt: collectionFieldsSource && collectionFieldsSource.musicCollectionBuiltAt
      ? collectionFieldsSource.musicCollectionBuiltAt
      : null,
    musicCollectionSummary: collectionFieldsSource && collectionFieldsSource.musicCollectionSummary
      ? collectionFieldsSource.musicCollectionSummary
      : null,
    practiceTrackBackend: activeCandidate && activeCandidate.practiceTrackBackend
      ? activeCandidate.practiceTrackBackend
      : null,
    snapcast: activeCandidate && activeCandidate.snapcast
      ? activeCandidate.snapcast
      : null,
    cast: activeCandidate && activeCandidate.cast
      ? activeCandidate.cast
      : null,
  };
}

export async function checkMediaResolverHealth(accessToken) {
  const status = await probeMediaResolverCandidates(accessToken);
  return status.available;
}

const HEAVY_ML_PATH_PREFIXES = [
  '/separate-stems',
  '/stems/',
  '/transcribe-sheet-image',
  '/extract-sheet-metadata',
  '/split-sheet-page',
  '/analyze-media',
  '/detect-chords',
  '/analyze-practice',
  '/generate-practice-track',
  '/generate-audio',
  '/render-midi',
];

function pathNeedsSnapcastPlayback(pathAndQuery) {
  const path = String(pathAndQuery || '').split('?')[0];
  return path.indexOf('/snapcast-playback/') === 0;
}

function pathNeedsCastPlayback(pathAndQuery) {
  const path = String(pathAndQuery || '').split('?')[0];
  return path.indexOf('/cast-playback/') === 0;
}

function pathNeedsHomeRemotePlayback(pathAndQuery) {
  return pathNeedsSnapcastPlayback(pathAndQuery) || pathNeedsCastPlayback(pathAndQuery);
}

function pathNeedsHeavyMl(pathAndQuery) {
  const path = String(pathAndQuery || '').split('?')[0];
  for (let i = 0; i < HEAVY_ML_PATH_PREFIXES.length; i++) {
    if (path.indexOf(HEAVY_ML_PATH_PREFIXES[i]) === 0) return true;
  }
  return false;
}

function pathNeedsYoutubeEgress(pathAndQuery) {
  const path = String(pathAndQuery || '').split('?')[0];
  return path.indexOf('/youtube/') === 0;
}

function pathNeedsScrapeProxy(pathAndQuery) {
  const path = String(pathAndQuery || '').split('?')[0];
  return path.indexOf('/search-chords') === 0
    || path.indexOf('/search-lyrics') === 0;
}

function pathNeedsMidiAnalyze(pathAndQuery) {
  const path = String(pathAndQuery || '').split('?')[0];
  return path.indexOf('/midi2analyze') === 0
    || path.indexOf('/midi2abc') === 0
    || path.indexOf('/midi2xml') === 0
    || path.indexOf('/midi-resources/') === 0;
}

function pathNeedsMusicCollection(pathAndQuery) {
  const endpoint = resolverEndpointForPath(pathAndQuery);
  return endpoint === 'search-music-collection'
    || endpoint === 'browse-music-collection'
    || endpoint === 'music-collection-duplicates'
    || endpoint === 'music-collection-triage'
    || endpoint === 'music-collection-triage-bulk'
    || endpoint === 'music-collection-move-plan'
    || endpoint === 'music-collection-registry'
    || endpoint === 'music-collection-by-entry'
    || endpoint === 'music-collection-artists'
    || endpoint === 'music-collection-albums'
    || endpoint === 'music-collection-genres'
    || endpoint === 'music-collection-tree'
    || endpoint === 'music-collection-chunks'
    || endpoint === 'rebuild-music-collection-index'
    || endpoint === 'music-collection'
    || endpoint === 'music-collection-art';
}

function pathNeedsBilling(pathAndQuery) {
  const path = String(pathAndQuery || '').split('?')[0];
  return path.indexOf('/billing/') === 0;
}

function pathNeedsBillingAdmin(pathAndQuery) {
  const path = String(pathAndQuery || '').split('?')[0];
  return path.indexOf('/billing/admin/') === 0;
}

function formatSnapcastPlaybackResolverError(error, bases) {
  const message = error && error.message ? String(error.message) : '';
  if (message.indexOf('Media proxy error 401') === 0
    || message.indexOf('Media proxy error 403') === 0) {
    throw new Error(
      'Sign in to Tune Book to route audio through your home resolver (Snapcast requires peppertrees).'
    );
  }
  if (getSnapcastPlaybackProxyBase()) {
    throw new Error(
      'Could not reach your home resolver for Snapcast (' + getSnapcastPlaybackProxyBase() + '). '
      + 'Check that docker compose --profile snapcast is running on peppertrees.'
    );
  }
  throw new Error(
    'No Snapcast-capable resolver is reachable. Snapcast needs your home resolver '
    + '(https://peppertrees.syntithenai.com), not the cloud fallback. '
    + 'Sign in, then try again.'
  );
}

function formatCastPlaybackResolverError(error, bases) {
  const message = error && error.message ? String(error.message) : '';
  const castBase = getCastPlaybackProxyBase();
  if (message.indexOf('Media proxy error 401') === 0
    || message.indexOf('Media proxy error 403') === 0) {
    throw new Error(
      'Sign in to Tune Book to route audio through your home resolver for Chromecast.'
    );
  }
  if (message.indexOf('Media proxy error 402') === 0) {
    throw new Error(
      'Hosted playback is not available for this account. Use your own local resolver for Chromecast.'
    );
  }
  if (castBase) {
    const extra = message && message.indexOf('Media proxy error') < 0 && message !== 'fetch failed'
      ? message
      : '';
    throw new Error(
      'Could not reach your home resolver for Chromecast (' + castBase + '). '
      + (extra ? extra + '. ' : '')
      + 'Check that your resolver is running, you are signed in, and CAST_PUBLIC_URL is set on that host.'
    );
  }
  throw new Error(
    'No Chromecast-capable resolver is reachable. Chromecast needs your home resolver with ffmpeg, '
    + 'not the cloud fallback. Sign in, then try again.'
  );
}

function formatRemotePlaybackResolverError(error, bases, pathAndQuery) {
  if (pathNeedsCastPlayback(pathAndQuery)) {
    formatCastPlaybackResolverError(error, bases);
    return;
  }
  if (pathNeedsSnapcastPlayback(pathAndQuery)) {
    formatSnapcastPlaybackResolverError(error, bases);
    return;
  }
  wrapFetchError(error, bases);
}

function resolvePreferredProxyBase(pathAndQuery) {
  if (pathNeedsSnapcastPlayback(pathAndQuery)) {
    const snapcastBase = getSnapcastPlaybackProxyBase();
    if (snapcastBase) return snapcastBase;
  }
  if (pathNeedsCastPlayback(pathAndQuery)) {
    const castBase = getCastPlaybackProxyBase();
    if (castBase) return castBase;
  }
  if (pathNeedsHeavyMl(pathAndQuery) && heavyMlProxyBase) {
    return heavyMlProxyBase;
  }
  if (pathNeedsMusicCollection(pathAndQuery) && musicCollectionProxyBase) {
    return musicCollectionProxyBase;
  }
  if (pathNeedsBillingAdmin(pathAndQuery) && billingAdminProxyBase) {
    return billingAdminProxyBase;
  }
  if (pathNeedsBilling(pathAndQuery) && billingProxyBase) {
    return billingProxyBase;
  }
  if (pathNeedsMidiAnalyze(pathAndQuery) && midiImportProxyBase) {
    return midiImportProxyBase;
  }
  return activeProxyBase;
}

export async function fetchViaMediaProxy(pathAndQuery, accessToken, requestOptions = {}) {
  const billingAdminPath = pathNeedsBillingAdmin(pathAndQuery);
  const billingPath = pathNeedsBilling(pathAndQuery);
  const preferredBase = resolvePreferredProxyBase(pathAndQuery);
  let bases;
  if (billingAdminPath && billingAdminProxyBase) {
    bases = [billingAdminProxyBase];
  } else if (billingPath && billingProxyBase) {
    bases = [billingProxyBase];
  } else if (preferredBase) {
    bases = [preferredBase].concat(getMediaProxyBaseCandidates().filter(function(b) { return b !== preferredBase; }));
  } else {
    bases = getMediaProxyBaseCandidates();
  }

  if (bases.length === 0) {
    throw new Error('Media proxy not configured');
  }
  if (billingAdminPath && !billingAdminProxyBase) {
    throw new Error(
      'No resolver with billing admin access is reachable. '
      + 'Sign in with an admin account and ensure ALLOWED_ADMIN_EMAILS is set on a running billing resolver.'
    );
  }
  if (billingPath && !billingProxyBase) {
    throw new Error(
      'No hosted resolver with billing is reachable. '
      + 'Credit purchases and balances are managed on the central Cloud Run resolver.'
    );
  }

  let tokenForRequest = accessToken;
  let didAuthRetry = false;

  async function attemptAllBases() {
    let lastError = null;
    for (let i = 0; i < bases.length; i++) {
      const proxyBase = bases[i];
      if (isMixedContentBlocked(proxyBase)) {
        continue;
      }
      if (pathNeedsHomeRemotePlayback(pathAndQuery)
        && isCloudLightResolverBase(proxyBase)
        && proxyBase !== getSnapcastPlaybackProxyBase()
        && proxyBase !== getCastPlaybackProxyBase()) {
        continue;
      }
      if (pathNeedsMidiAnalyze(pathAndQuery)
        && isCloudLightResolverBase(proxyBase)
        && proxyBase !== midiImportProxyBase) {
        continue;
      }
      if (pathNeedsMusicCollection(pathAndQuery)
        && isCloudLightResolverBase(proxyBase)
        && proxyBase !== musicCollectionProxyBase) {
        continue;
      }
      const url = proxyBase + pathAndQuery;
      try {
        const mergedHeaders = Object.assign(
          {},
          buildAuthHeaders(tokenForRequest),
          getActiveProviderHeaders(loadProviderSettings(), {
            aiEnabled: candidateAiEnabled(proxyBase),
          }),
          pathNeedsYoutubeEgress(pathAndQuery) ? getYoutubeEgressHeaders() : {},
          pathNeedsScrapeProxy(pathAndQuery) ? getScrapeProxyHeaders() : {},
          requestOptions.headers || {}
        );
        const response = await fetch(url, {
          ...requestOptions,
          headers: mergedHeaders,
        });
        if (response.ok) {
          activeProxyBase = proxyBase;
          trackResolverRequest(resolverEndpointForPath(pathAndQuery));
          return response;
        }
        let detail = '';
        let hint = '';
        try {
          const body = await response.json();
          detail = body.error || body.detail || '';
          if (detail && typeof detail !== 'string') {
            detail = JSON.stringify(detail);
          }
          hint = body.hint || '';
        } catch (e) {}
        const proxyError = new Error(
          'Media proxy error ' + response.status
          + (detail ? ': ' + detail : '')
          + (hint ? ' (' + hint + ')' : '')
        );
        proxyError.status = response.status;
        if (billingAdminPath) {
          throw proxyError;
        }
        if (response.status === 404
          && pathNeedsMusicCollection(pathAndQuery)
          && musicCollectionProxyBase
          && proxyBase === musicCollectionProxyBase) {
          throw new Error(
            'Media proxy error 404: Music collection browse API not found on '
            + musicCollectionProxyBase
            + '. Rebuild and restart your home local-resolver container.'
          );
        }
        if ((response.status === 401 || response.status === 403 || response.status === 404 || response.status === 405) && i < bases.length - 1) {
          lastError = proxyError;
          activeProxyBase = null;
          continue;
        }
        throw proxyError;
      } catch (error) {
        lastError = error;
        if (error && error.message && error.message.indexOf('Media proxy error') === 0) {
          if (error.message.indexOf('Media proxy error 401') === 0
            || error.message.indexOf('Media proxy error 403') === 0
            || error.message.indexOf('Media proxy error 405') === 0) {
            activeProxyBase = null;
            if (i < bases.length - 1) continue;
          }
          throw error;
        }
      }
    }
    if (pathNeedsHomeRemotePlayback(pathAndQuery)) {
      formatRemotePlaybackResolverError(lastError || new Error('fetch failed'), bases, pathAndQuery);
    }
    if (pathNeedsMusicCollection(pathAndQuery)) {
      const message = lastError && lastError.message ? String(lastError.message) : '';
      if (message.indexOf('Media proxy error 404') === 0) {
        throw new Error(
          'Music collection browse API was not found on your home resolver. '
          + 'Rebuild and restart the local-resolver container (docker compose build local-resolver && docker compose up -d local-resolver).'
        );
      }
    }
    if (billingAdminPath) {
      const message = lastError && lastError.message ? String(lastError.message) : '';
      if (message.indexOf('Media proxy error 403') === 0) {
        throw new Error('Admin access required on the billing resolver. Check ALLOWED_ADMIN_EMAILS on that host.');
      }
      if (message.indexOf('Media proxy error 404') === 0) {
        throw new Error(
          'Billing admin API is not available on '
          + (billingAdminProxyBase || 'the resolver')
          + '. Deploy the updated resolver or use a host that supports /billing/admin.'
        );
      }
      const adminBase = billingAdminProxyBase || bases[0] || 'the billing resolver';
      throw new Error(
        'Could not reach the billing admin resolver (' + adminBase + '). '
        + 'Ensure that resolver is running and your account is in ALLOWED_ADMIN_EMAILS.'
      );
    }
    wrapFetchError(lastError || new Error('fetch failed'), bases);
  }

  try {
    return await attemptAllBases();
  } catch (error) {
    const is401 = error && (
      error.status === 401
      || (error.message && error.message.indexOf('Media proxy error 401') === 0)
    );
    if (is401 && !didAuthRetry) {
      didAuthRetry = true;
      const refreshed = await tryRefreshAccessToken();
      if (refreshed && refreshed.access_token) {
        tokenForRequest = refreshed.access_token;
        return attemptAllBases();
      }
    }
    throw error;
  }
}

const inFlightAudioProxyFetches = new Map();

function shouldCoalesceMediaProxyAudio(pathAndQuery) {
  const path = String(pathAndQuery || '');
  return path.indexOf('/bandcamp/audio') === 0
    || path.indexOf('/internet-archive/audio') === 0
    || path.indexOf('/loc/audio') === 0
    || path.indexOf('/proxy-audio') === 0
    || /^\/youtube\/[^/]+\/audio(?:\?|$)/.test(path);
}

function mediaProxyAudioCoalesceKey(pathAndQuery, accessToken) {
  return String(pathAndQuery || '') + '\0' + String(accessToken || '');
}

async function readResponseArrayBuffer(response) {
  if (response && typeof response.arrayBuffer === 'function') {
    return response.arrayBuffer();
  }
  if (response && typeof response.blob === 'function') {
    const blob = await response.blob();
    if (blob && typeof blob.arrayBuffer === 'function') {
      return blob.arrayBuffer();
    }
  }
  return new ArrayBuffer(0);
}

function responseFromBufferedAudio(buffer, contentType, status) {
  const copy = buffer && buffer.slice ? buffer.slice(0) : new ArrayBuffer(0);
  const mime = sniffAudioMimeFromBytes(bytesPrefixFromBuffer(copy, 16))
    || declaredAudioMime(contentType)
    || 'audio/mpeg';
  return {
    ok: true,
    status: status || 200,
    headers: {
      get: function(name) {
        return String(name).toLowerCase() === 'content-type' ? mime : null;
      },
    },
    arrayBuffer: async function() { return copy.slice(0); },
    blob: async function() { return new Blob([copy], { type: mime }); },
  };
}

/** Share one yt-dlp stream body between playback and cache jobs. */
async function fetchViaMediaProxyCoalesced(pathAndQuery, accessToken) {
  if (!shouldCoalesceMediaProxyAudio(pathAndQuery)) {
    return fetchViaMediaProxy(pathAndQuery, accessToken);
  }
  const key = mediaProxyAudioCoalesceKey(pathAndQuery, accessToken);
  const existing = inFlightAudioProxyFetches.get(key);
  if (existing) {
    const buffered = await existing;
    return responseFromBufferedAudio(buffered.buffer, buffered.contentType, buffered.status);
  }
  const pending = (async function() {
    const response = await fetchViaMediaProxy(pathAndQuery, accessToken);
    const buffer = await readResponseArrayBuffer(response);
    const contentType = response && response.headers && typeof response.headers.get === 'function'
      ? response.headers.get('Content-Type')
      : '';
    return {
      buffer: buffer,
      contentType: contentType || '',
      status: response && response.status ? response.status : 200,
    };
  })();
  inFlightAudioProxyFetches.set(key, pending);
  try {
    const buffered = await pending;
    return responseFromBufferedAudio(buffered.buffer, buffered.contentType, buffered.status);
  } finally {
    inFlightAudioProxyFetches.delete(key);
  }
}

async function tryDirectFetch(url) {
  try {
    const response = await fetch(url);
    if (response.ok) {
      return response;
    }
  } catch (e) {
    // direct fetch blocked (e.g. CORS)
  }
  return null;
}

function isPrivateOrLocalHost(hostname) {
  const host = String(hostname || '').toLowerCase();
  if (!host || host === 'localhost' || host === '127.0.0.1') return true;
  if (host.startsWith('192.168.') || host.startsWith('10.')) return true;
  if (/^172\.(1[6-9]|2\d|3[0-1])\./.test(host)) return true;
  return false;
}

/**
 * Resolver /proxy-audio only accepts https upstream URLs. Upgrade public http://
 * links (archive.org, bandcamp, direct .mp3, remote MIDI, etc.). Local music-
 * collection URLs keep http when they point at a home resolver host.
 */
export function normalizeMediaProxyTargetUrl(url) {
  const trimmed = String(url || '').trim();
  if (!trimmed || !/^http:\/\//i.test(trimmed)) return trimmed;
  if (isMusicCollectionLinkUri(trimmed) || isMusicCollectionByEntryUri(trimmed)) return trimmed;
  try {
    const parsed = new URL(trimmed);
    if (isPrivateOrLocalHost(parsed.hostname)) return trimmed;
    parsed.protocol = 'https:';
    return parsed.toString();
  } catch (e) {
    return trimmed;
  }
}

export function sniffAudioMimeFromBytes(bytes) {
  const b = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes || []);
  if (b.length >= 3 && b[0] === 0x49 && b[1] === 0x44 && b[2] === 0x33) return 'audio/mpeg';
  if (b.length >= 2 && b[0] === 0xff && (b[1] & 0xe0) === 0xe0) return 'audio/mpeg';
  if (b.length >= 4 && b[0] === 0x4f && b[1] === 0x67 && b[2] === 0x67 && b[3] === 0x53) {
    return 'audio/ogg';
  }
  if (b.length >= 4 && b[0] === 0x66 && b[1] === 0x4c && b[2] === 0x61 && b[3] === 0x43) {
    return 'audio/flac';
  }
  if (b.length >= 12 && b[4] === 0x66 && b[5] === 0x74 && b[6] === 0x79 && b[7] === 0x70) {
    const brand = b.length >= 12
      ? String.fromCharCode(b[8] || 0, b[9] || 0, b[10] || 0, b[11] || 0)
      : '';
    if (brand === 'M4A ' || brand === 'M4B ') return 'audio/x-m4a';
    return 'audio/mp4';
  }
  if (b.length >= 4 && b[0] === 0x52 && b[1] === 0x49 && b[2] === 0x46 && b[3] === 0x46) {
    return 'audio/wav';
  }
  if (b.length >= 4 && b[0] === 0x1a && b[1] === 0x45 && b[2] === 0xdf && b[3] === 0xa3) {
    return 'audio/webm';
  }
  return '';
}

function declaredAudioMime(contentType) {
  const declared = String(contentType || '').split(';')[0].trim().toLowerCase();
  if (declared.indexOf('audio/') === 0 || declared.indexOf('video/') === 0) return declared;
  return '';
}

function bytesPrefixFromBuffer(buffer, length) {
  const max = length || 16;
  if (!buffer) return new Uint8Array(0);
  if (buffer instanceof Uint8Array) {
    return buffer.subarray(0, Math.min(max, buffer.length));
  }
  if (ArrayBuffer.isView(buffer)) {
    return new Uint8Array(buffer.buffer, buffer.byteOffset, Math.min(max, buffer.byteLength));
  }
  try {
    const view = new Uint8Array(buffer);
    return view.subarray(0, Math.min(max, view.length));
  } catch (e) {
    return new Uint8Array(0);
  }
}

function readBlobArrayBufferViaFileReader(blob) {
  return new Promise(function(resolve) {
    if (typeof FileReader === 'undefined' || !blob) {
      resolve(new ArrayBuffer(0));
      return;
    }
    const reader = new FileReader();
    reader.onload = function() {
      resolve(reader.result || new ArrayBuffer(0));
    };
    reader.onerror = function() {
      resolve(new ArrayBuffer(0));
    };
    try {
      reader.readAsArrayBuffer(blob);
    } catch (e) {
      resolve(new ArrayBuffer(0));
    }
  });
}

async function readBlobArrayBuffer(blob) {
  if (blob && typeof blob.arrayBuffer === 'function') {
    try {
      const buffer = await blob.arrayBuffer();
      if (buffer && buffer.byteLength) return buffer;
    } catch (e) {}
  }
  if (blob && blob.size > 0) {
    return readBlobArrayBufferViaFileReader(blob);
  }
  return new ArrayBuffer(0);
}

/** Alternate MIME types to try when HTML <audio> rejects the sniffed type (error 4). */
export function htmlAudioMimeFallbackTypes(currentType) {
  const current = String(currentType || '').split(';')[0].trim().toLowerCase();
  const mp4Family = current === 'audio/mp4'
    || current === 'audio/x-m4a'
    || current === 'audio/m4a'
    || current === 'audio/aac'
    || current === 'audio/mp4a-latm';
  const candidates = mp4Family
    ? ['audio/x-m4a', 'audio/aac', 'audio/mp4', 'audio/mpeg']
    : ['audio/mpeg', 'audio/mp4', 'audio/x-m4a', 'audio/aac'];
  return candidates.filter(function(type) { return type !== current; });
}

/** Bandcamp lossless m4a is often ALAC, which HTML <audio> cannot play. */
export function looksLikeAlacAudio(bytes) {
  const b = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes || []);
  if (b.length < 12) return false;
  const hasFtyp = b[4] === 0x66 && b[5] === 0x74 && b[6] === 0x79 && b[7] === 0x70;
  if (!hasFtyp) return false;
  const limit = Math.min(b.length, 262144);
  for (let i = 8; i < limit - 3; i++) {
    if (b[i] === 0x61 && b[i + 1] === 0x6c && b[i + 2] === 0x61 && b[i + 3] === 0x63) {
      return true;
    }
  }
  return false;
}

/** yt-dlp streams arrive as octet-stream; HTML audio needs a real audio MIME type. */
export async function blobForHtmlAudioPlayback(blob, contentType) {
  if (!blob) return blob;
  const buffer = await readBlobArrayBuffer(blob);
  const sniffed = sniffAudioMimeFromBytes(bytesPrefixFromBuffer(buffer, 16));
  const declared = declaredAudioMime(contentType) || declaredAudioMime(blob.type);
  const mime = sniffed || declared || 'audio/mpeg';
  if (buffer && buffer.byteLength) {
    return new Blob([buffer], { type: mime });
  }
  return new Blob([blob], { type: mime });
}

export async function fetchDirectOrProxy(options) {
  const { srcType, youtubeGetId, accessToken, resolveDirectUrl, collectionLink } = options;
  const src = normalizeMediaProxyTargetUrl(options.src);

  if (srcType === 'youtube') {
    const videoId = youtubeGetId(src);
    if (!videoId) throw new Error('Invalid YouTube URL');

    if (isMediaProxyConfigured()) {
      const response = await fetchViaMediaProxyCoalesced(
        '/youtube/' + encodeURIComponent(videoId) + '/audio',
        accessToken
      );
      return { response: response, viaProxy: true };
    }

    if (resolveDirectUrl) {
      const directUrl = await resolveDirectUrl(src, srcType, youtubeGetId);
      if (directUrl) {
        const response = await tryDirectFetch(directUrl);
        if (response) {
          return { response: response, viaProxy: false };
        }
      }
    }

    throw new Error(
      'Could not resolve YouTube audio stream (install the TuneBook Helper extension, or configure a media resolver)'
    );
  }

  if (isBandcampLinkUri(src)) {
    if (!isMediaProxyConfigured()) {
      throw new Error('Bandcamp playback requires a configured media resolver');
    }
    const bandcampUrl = repairBandcampLinkUri(src)
    const response = await fetchViaMediaProxyCoalesced(
      '/bandcamp/audio?url=' + encodeURIComponent(bandcampUrl),
      accessToken
    );
    return { response: response, viaProxy: true };
  }

  if (isArchiveOrgLinkUri(src)) {
    if (!isMediaProxyConfigured()) {
      throw new Error('Internet Archive playback requires a configured media resolver');
    }
    if (isArchiveOrgDirectDownloadUri(src)) {
      const response = await fetchViaMediaProxyCoalesced(
        '/proxy-audio?url=' + encodeURIComponent(src),
        accessToken
      );
      return { response: response, viaProxy: true };
    }
    const response = await fetchViaMediaProxyCoalesced(
      '/internet-archive/audio?url=' + encodeURIComponent(src),
      accessToken
    );
    return { response: response, viaProxy: true };
  }

  if (isLocGovLinkUri(src)) {
    if (!isMediaProxyConfigured()) {
      throw new Error('Library of Congress playback requires a configured media resolver');
    }
    const response = await fetchViaMediaProxyCoalesced(
      '/loc/audio?url=' + encodeURIComponent(src),
      accessToken
    );
    return { response: response, viaProxy: true };
  }

  if (isOwnedMediaLinkUri(src)) {
    throw new Error('Owned recording links must be resolved locally before proxy playback');
  }

  if (isMusicCollectionLinkUri(src) || isMusicCollectionByEntryUri(src) || (collectionLink && collectionLink.collectionEntryId)) {
    if (!isMediaProxyConfigured()) {
      throw new Error('Music collection playback requires a configured media resolver');
    }
    const proxyPath = collectionLink
      ? musicCollectionPlaybackProxyPathFromLink(collectionLink)
      : musicCollectionPlaybackProxyPathFromUri(src);
    if (!proxyPath) {
      throw new Error('Invalid music collection link');
    }
    const response = await fetchViaMediaProxy(proxyPath, accessToken);
    return { response: response, viaProxy: true };
  }

  const directResponse = await tryDirectFetch(src);
  if (directResponse) {
    return { response: directResponse, viaProxy: false };
  }

  if (!isMediaProxyConfigured()) {
    throw new Error('Direct fetch failed and media proxy is not configured');
  }

  const response = await fetchViaMediaProxyCoalesced(
    '/proxy-audio?url=' + encodeURIComponent(src),
    accessToken
  );
  return { response: response, viaProxy: true };
}

/** True when the browser cannot play the link URL directly (resolver auth required). */
export function requiresResolverProxiedPlayback(src) {
  const trimmed = String(src || '').trim();
  if (!trimmed) return false;
  return isMusicCollectionLinkUri(trimmed)
    || isMusicCollectionByEntryUri(trimmed)
    || isBandcampLinkUri(trimmed)
    || isArchiveOrgLinkUri(trimmed)
    || isLocGovLinkUri(trimmed);
}

/** Fetch resolver/direct audio as a playable Blob (no decode/re-encode). */
export async function fetchPlayableAudioBlob(src, srcType, options) {
  const opts = options || {};
  const { response } = await fetchDirectOrProxy({
    src: src,
    srcType: srcType,
    youtubeGetId: opts.youtubeGetId,
    accessToken: opts.accessToken,
    resolveDirectUrl: opts.resolveDirectUrl,
    collectionLink: opts.collectionLink,
  });
  const rawBlob = await response.blob();
  if (!rawBlob || !rawBlob.size) {
    throw new Error('Could not load audio');
  }
  const mime = response.headers && typeof response.headers.get === 'function'
    ? response.headers.get('Content-Type')
    : null;
  return blobForHtmlAudioPlayback(rawBlob, mime);
}

/** Fetch audio through the media resolver and return a blob: URL for <audio> playback. */
export async function fetchProxiedAudioBlobUrl(src, srcType, options) {
  const blob = await fetchPlayableAudioBlob(src, srcType, options);
  return URL.createObjectURL(blob);
}

export function describeResolverAuthReason(authReason) {
  if (authReason === 'login_required') return 'Login required';
  if (authReason === 'email_not_authorized') return 'Google account not authorized';
  if (authReason === 'resolver_access_denied') return 'Resolver access denied for this account';
  if (authReason === 'invalid_token') return 'Login expired or invalid';
  if (authReason === 'insufficient_credit') return 'Hosted access unavailable for this account';
  return '';
}

/**
 * Block starting new resolver-proxied playback when billing requires credit and balance is empty.
 * Returns { message } or null.
 */
export function getResolverProxiedPlaybackBlock(resolverStatus, accessToken) {
  if (!resolverStatus || !isMediaProxyConfigured()) return null;
  if (!accessToken) return null;
  if (resolverStatus.creditUnlimited) return null;
  if (!resolverStatus.billingEnabled) return null;

  const balance = typeof resolverStatus.creditBalanceCents === 'number'
    ? resolverStatus.creditBalanceCents
    : null;
  const creditRequired = !!resolverStatus.creditRequired;
  const insufficientCandidates = (resolverStatus.candidates || []).filter(function(candidate) {
    return candidate.authReason === 'insufficient_credit';
  });
  const insufficientFromAuth = insufficientCandidates.length > 0;

  const shouldBlock = (creditRequired && balance !== null && balance <= 0) || insufficientFromAuth;
  if (shouldBlock) {
    return {
      message: 'Hosted playback is not available for this account. Use your own local resolver instead.',
    };
  }
  return null;
}

/**
 * Credit purchasing is disabled, so no low-balance purchase prompt is shown.
 */
export function getResolverCreditLowBalanceWarning() {
  return null;
}

/**
 * When shared resolver providers are reachable but blocked on auth.
 * Returns { message, showLoginButton } or null.
 */
export function getResolverLoginWarning(resolverStatus, accessToken) {
  if (isNavigatorOffline()) return null;
  if (!resolverStatus || resolverStatus.available) return null;

  const candidates = resolverStatus.candidates || [];
  const authBlocked = candidates.filter(function(candidate) {
    return candidate.reachable && candidate.requireAuth && !candidate.available;
  });
  if (authBlocked.length === 0) return null;

  const hasToken = !!accessToken;
  const loginRequired = authBlocked.some(function(candidate) {
    return candidate.authReason === 'login_required' || (!candidate.authReason && !hasToken);
  });
  const invalidToken = authBlocked.some(function(candidate) {
    return candidate.authReason === 'invalid_token';
  });
  const notAuthorized = authBlocked.some(function(candidate) {
    return candidate.authReason === 'email_not_authorized';
  });

  if (loginRequired && !hasToken) {
    return {
      message: 'Login to continue',
      showLoginButton: true,
    };
  }
  // Client has a token but /health still reports login_required — usually a
  // stale pre-login probe. Do not flash "not available / please login".
  if (
    loginRequired
    && hasToken
    && !invalidToken
    && !notAuthorized
    && !authBlocked.some(function(candidate) {
      return candidate.authReason === 'insufficient_credit';
    })
  ) {
    return null;
  }
  if (invalidToken) {
    return {
      message: 'Your Google login has expired. Sign in again to use shared resolver providers.',
      showLoginButton: true,
    };
  }
  const insufficientCredit = authBlocked.some(function(candidate) {
    return candidate.authReason === 'insufficient_credit';
  });
  if (insufficientCredit && hasToken) {
    return {
      message: 'Hosted features are not available for this account.',
      showLoginButton: false,
      showBuyCreditButton: false,
    };
  }
  if (notAuthorized) {
    return {
      message: 'Your Google account is not authorized on the shared resolver. Add your own API keys under Providers, or run a local resolver.',
      showLoginButton: false,
    };
  }

  return {
    message: 'Shared resolver providers are reachable but not available to this account. Log in with an authorized Google account or configure your own API keys.',
    showLoginButton: !hasToken,
  };
}

export function isMediaProxyAuthorizationError(error) {
  const message = error && error.message ? String(error.message) : '';
  if (!message) return false;
  return message.indexOf('Media proxy error 401') === 0
    || message.indexOf('Missing Authorization') >= 0
    || message.indexOf('Invalid or expired Google token') >= 0;
}

export function isMediaResolverInfrastructureError(error) {
  const message = error && error.message ? String(error.message) : '';
  if (!message) return false;
  if (message.indexOf('Could not reach any media resolver') >= 0) return true;
  if (message.indexOf('Could not reach the media resolver') >= 0) return true;
  if (message.indexOf('mixed content') >= 0) return true;
  if (message.indexOf('Media proxy not configured') >= 0) return true;
  if (message.indexOf('Media proxy error 404') >= 0) return true;
  if (message.indexOf('Media proxy error 502') >= 0) return true;
  if (message.indexOf('Media proxy error 503') >= 0) return true;
  if (message.indexOf('Media proxy error 504') >= 0) return true;
  if (message === 'Network Error') return true;
  if (message.indexOf('Failed to fetch') >= 0) return true;
  return false;
}

export function isNotationSearchEmptyError(error) {
  const message = error && error.message ? String(error.message) : '';
  if (!message) return false;
  return message.indexOf('No ABC notation found') >= 0
    || message.indexOf('No notation found') >= 0;
}
